import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import { usePasswordVisibility } from '../../hooks/usePasswordVisibility';

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  isSubmitting: boolean;
  error: string | null;
}

export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  error,
}) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [confirmationText, setConfirmationText] = useState('');
  const [password, setPassword] = useState('');
  const { showPassword, inputType: passwordInputType, toggleVisibility: togglePasswordVisibility } = usePasswordVisibility();

  const isDeleteConfirmed = confirmationText === 'DELETE';
  const canSubmit = isDeleteConfirmed && password.length > 0 && !isSubmitting;

  const handleClose = () => {
    if (!isSubmitting) {
      setConfirmationText('');
      setPassword('');
      onClose();
    }
  };

  const handleSubmit = () => {
    if (canSubmit) {
      onConfirm(password);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        Delete Your Account
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          This action is irreversible. Your account and all associated data will be permanently
          deleted after a 30-day grace period.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          During the grace period you will be signed out and unable to log in. You can contact
          support to cancel the deletion within 30 days.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Type DELETE to confirm"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            disabled={isSubmitting}
            autoFocus
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            type={passwordInputType}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={!isDeleteConfirmed || isSubmitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type="button"
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                    disabled={!isDeleteConfirmed || isSubmitting}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          disabled={!canSubmit}
        >
          {isSubmitting ? 'Deleting...' : 'Delete My Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
